import React from 'react';

const SCHEMA_TABLES = [
  {
    name: 'User',
    fields: [
      { name: 'id', type: 'UUID', isKey: true },
      { name: 'name', type: 'String' },
      { name: 'email', type: 'String' },
    ],
  },
  {
    name: 'Post',
    fields: [
      { name: 'id', type: 'UUID', isKey: true },
      { name: 'title', type: 'String' },
      { name: 'content', type: 'Text' },
      { name: 'userId', type: 'UUID', isFk: true },
    ],
  },
  {
    name: 'Comment',
    fields: [
      { name: 'id', type: 'UUID', isKey: true },
      { name: 'text', type: 'Text' },
      { name: 'postId', type: 'UUID', isFk: true },
    ],
  },
];

const LIVE_ENDPOINTS = [
  { method: 'GET', path: '/api/users' },
  { method: 'POST', path: '/api/users' },
  { method: 'GET', path: '/api/users/{id}' },
  { divider: true },
  { method: 'GET', path: '/api/posts' },
  { method: 'POST', path: '/api/posts' },
  { method: 'GET', path: '/api/posts/{id}' },
  { divider: true },
  { method: 'GET', path: '/api/comments' },
  { method: 'POST', path: '/api/comments' },
  { divider: true },
  { method: 'GET', path: '/docs', label: 'Interactive API docs' },
];

function GlobeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M1.5 7h11M7 1.5c1.7 2 1.7 9 0 11M7 1.5c-1.7 2-1.7 9 0 11" />
    </svg>
  );
}

function MockTable({ data }) {
  return (
    <div className="pic-table">
      <div className="pic-table-head">{data.name}</div>
      {data.fields.map((f) => (
        <div key={f.name} className={`pic-row${f.isKey ? ' is-key' : ''}${f.isFk ? ' is-fk' : ''}`}>
          <span className="pic-field-name">{f.name}</span>
          <span className="pic-field-type">{f.type}</span>
        </div>
      ))}
    </div>
  );
}

const DEFAULT_RELATIONS = [
  { d: 'M 35 22 Q 50 22 50 42' },
  { d: 'M 50 58 Q 50 72 65 78' },
];

function SchemaCard({ relations = DEFAULT_RELATIONS, relationDashArray = '1.5 1.5', relationStrokeWidth = 0.5 }) {
  return (
    <div className="show-card diagram-card">
      <div className="diagram-tables">
        {SCHEMA_TABLES.map((t) => (
          <MockTable key={t.name} data={t} />
        ))}
      </div>
      <svg
        className="diagram-lines"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {relations.map((rel, i) => (
          <path
            key={i}
            d={rel.d}
            stroke="var(--accent)"
            strokeWidth={relationStrokeWidth}
            fill="none"
            strokeDasharray={relationDashArray || undefined}
            strokeLinecap="round"
          />
        ))}
      </svg>
    </div>
  );
}

function LiveApiCard() {
  return (
    <div className="show-card api-card">
      <div className="api-card-header">
        <span className="api-globe">
          <GlobeIcon />
        </span>
        <span className="api-url">myapi.backora.dev</span>
        <span className="api-status">
          <span className="api-status-dot" />
          LIVE
        </span>
      </div>
      <div className="api-card-body">
        {LIVE_ENDPOINTS.map((row, i) => {
          if (row.divider) {
            return <div key={`d-${i}`} className="api-divider" />;
          }
          return (
            <div key={i} className="api-row">
              <span className={`api-method ${row.method.toLowerCase()}`}>{row.method}</span>
              <span className="api-path">{row.path}</span>
              {row.label && <span className="api-label">{row.label}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VisionShowcase({
  leftLabel = 'Your schema',
  rightLabel = 'Live API',
  variant = 'side-by-side',
  kitePath = 'M 54 32 C 62 44, 54 58, 62 66',
  kiteArrowPath = 'M 61 64 L 62 66 L 60 65',
  kiteStrokeWidth = 0.45,
  kiteDashArray = '1.6 1.4',
  schemaRelations,
  schemaRelationDashArray,
  schemaRelationStrokeWidth,
}) {
  const schemaProps = {
    ...(schemaRelations !== undefined && { relations: schemaRelations }),
    ...(schemaRelationDashArray !== undefined && { relationDashArray: schemaRelationDashArray }),
    ...(schemaRelationStrokeWidth !== undefined && { relationStrokeWidth: schemaRelationStrokeWidth }),
  };
  if (variant === 'stacked') {
    return (
      <div className="vision-showcase stacked">
        <svg
          className="stacked-line"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="kiteGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <path
            d={kitePath}
            stroke="url(#kiteGrad)"
            strokeWidth={kiteStrokeWidth}
            fill="none"
            strokeDasharray={kiteDashArray || undefined}
            strokeLinecap="round"
          />
          <path
            d={kiteArrowPath}
            stroke="var(--accent)"
            strokeWidth={kiteStrokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className="stacked-schema">
          <div className="show-label">{leftLabel}</div>
          <SchemaCard {...schemaProps} />
        </div>

        <div className="stacked-project">
          <div className="show-label">{rightLabel}</div>
          <LiveApiCard />
        </div>
      </div>
    );
  }

  return (
    <div className="vision-showcase">
      <div className="show-side">
        <div className="show-label">{leftLabel}</div>
        <SchemaCard />
      </div>

      <div className="show-arrow" aria-hidden="true">
        <svg viewBox="0 0 100 24" fill="none">
          <defs>
            <linearGradient id="arrowGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="1" />
            </linearGradient>
          </defs>
          <path d="M 2 12 L 92 12" stroke="url(#arrowGrad)" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 84 4 L 94 12 L 84 20" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>

      <div className="show-side">
        <div className="show-label">{rightLabel}</div>
        <LiveApiCard />
      </div>
    </div>
  );
}
